import { lazy } from 'react';
import { createBrowserRouter, Navigate, useLocation } from 'react-router';
import { Shell } from './Shell';
import { SignIn } from './SignIn';
import { TodayWorkspacePage as Today } from '@/today/Workspace';

const ChangePreview = lazy(() => import('@/changes/ChangePreview').then(module => ({ default: module.ChangePreview })));
const Changes = lazy(() => import('@/changes/Changes').then(module => ({ default: module.Changes })));
const Projects = lazy(() => import('@/projects/Projects').then(module => ({ default: module.Projects })));
const ProjectPage = lazy(() => import('@/projects/ProjectPage').then(module => ({ default: module.ProjectPage })));
const ChangePage = lazy(() => import('@/changes/ChangePage').then(module => ({ default: module.ChangePage })));
const Availability = lazy(() => import('@/settings/Availability').then(module => ({ default: module.Availability })));
const MailRules = lazy(() => import('@/rules/MailRules').then(module => ({ default: module.MailRules })));
const Directions = lazy(() => import('@/rules/Directions').then(module => ({ default: module.Directions })));
const Registers = lazy(() => import('@/rules/Registers').then(module => ({ default: module.Registers })));
const Institutions = lazy(() => import('@/institutions/Institutions').then(module => ({ default: module.Institutions })));
const InstitutionPage = lazy(() => import('@/institutions/Institutions').then(module => ({ default: module.InstitutionPage })));
const Threads = lazy(() => import('@/mail/Threads').then(module => ({ default: module.Threads })));
const ThreadPage = lazy(() => import('@/mail/ThreadPage').then(module => ({ default: module.ThreadPage })));
const Strangers = lazy(() => import('@/mail/Strangers').then(module => ({ default: module.Strangers })));
const Companies = lazy(() => import('@/directory/Directory').then(module => ({ default: module.Companies })));
const Contacts = lazy(() => import('@/directory/Directory').then(module => ({ default: module.Contacts })));
const CompanyPage = lazy(() => import('@/directory/Records').then(module => ({ default: module.CompanyPage })));
const ContactPage = lazy(() => import('@/directory/Records').then(module => ({ default: module.ContactPage })));
const FlexList = lazy(() => import('@/records/Flex').then(module => ({ default: module.FlexList })));
const FlexRecord = lazy(() => import('@/records/Flex').then(module => ({ default: module.FlexRecord })));
const Requests = lazy(() => import('@/requests/Requests').then(module => ({ default: module.Requests })));
const RequestPage = lazy(() => import('@/requests/RequestPage').then(module => ({ default: module.RequestPage })));
const Activity = lazy(() => import('@/requests/Activity').then(module => ({ default: module.Activity })));
const Approvals = lazy(() => import('@/oversight/Approvals').then(module => ({ default: module.Approvals })));
const AgentPage = lazy(() => import('@/oversight/Agents').then(module => ({ default: module.AgentPage })));
const Agents = lazy(() => import('@/oversight/Agents').then(module => ({ default: module.Agents })));
const Assignments = lazy(() => import('@/oversight/Assignments').then(module => ({ default: module.Assignments })));
const DepartmentPage = lazy(() => import('@/oversight/Departments').then(module => ({ default: module.DepartmentPage })));
const Departments = lazy(() => import('@/oversight/Departments').then(module => ({ default: module.Departments })));
const Safety = lazy(() => import('@/oversight/Safety').then(module => ({ default: module.Safety })));
const Tools = lazy(() => import('@/oversight/Tools').then(module => ({ default: module.Tools })));
const Connections = lazy(() => import('@/oversight/Connections').then(module => ({ default: module.Connections })));
const Operations = lazy(() => import('@/operations/Operations').then(module => ({ default: module.Operations })));
const Plans = lazy(() => import('@/oversight/Plans').then(module => ({ default: module.Plans })));
const Readiness = lazy(() => import('@/oversight/Readiness').then(module => ({ default: module.Readiness })));
const Workspace = lazy(() => import('@/oversight/Workspace').then(module => ({ default: module.Workspace })));
const Audit = lazy(() => import('@/oversight/Audit').then(module => ({ default: module.Audit })));
const Reports = lazy(() => import('@/oversight/Reports').then(module => ({ default: module.Reports })));
const Settings = lazy(() => import('@/mine/Settings').then(module => ({ default: module.Settings })));
const Promotions = lazy(() => import('@/mine/Promotions').then(module => ({ default: module.Promotions })));
const Notifications = lazy(() => import('@/notices/Notifications').then(module => ({ default: module.Notifications })));
const IncidentPage = lazy(() => import('@/notices/Incidents').then(module => ({ default: module.IncidentPage })));
const Incidents = lazy(() => import('@/notices/Incidents').then(module => ({ default: module.Incidents })));
const People = lazy(() => import('@/people/People').then(module => ({ default: module.People })));
const PersonPage = lazy(() => import('@/people/People').then(module => ({ default: module.PersonPage })));
const ErasurePage = lazy(() => import('@/people/Erasure').then(module => ({ default: module.ErasurePage })));
const Erasures = lazy(() => import('@/people/Erasure').then(module => ({ default: module.Erasures })));
const LedgerProposalPage = lazy(() => import('@/ledger/Proposals').then(module => ({ default: module.LedgerProposalPage })));
const LedgerProposals = lazy(() => import('@/ledger/Proposals').then(module => ({ default: module.LedgerProposals })));
const Constitution = lazy(() => import('@/ledger/Ledger').then(module => ({ default: module.Constitution })));
const Decision = lazy(() => import('@/ledger/Ledger').then(module => ({ default: module.Decision })));
const Decisions = lazy(() => import('@/ledger/Ledger').then(module => ({ default: module.Decisions })));
const Markets = lazy(() => import('@/prospecting/Markets').then(module => ({ default: module.Markets })));
const MarketPage = lazy(() => import('@/prospecting/MarketPage').then(module => ({ default: module.MarketPage })));
const ReportDoc = lazy(() => import('@/prospecting/ReportDoc').then(module => ({ default: module.ReportDoc })));
const ProfilePage = lazy(() => import('@/prospecting/Profiles').then(module => ({ default: module.ProfilePage })));
const Profiles = lazy(() => import('@/prospecting/Profiles').then(module => ({ default: module.Profiles })));
const TerritoryPage = lazy(() => import('@/prospecting/Territories').then(module => ({ default: module.TerritoryPage })));
const Territories = lazy(() => import('@/prospecting/Territories').then(module => ({ default: module.Territories })));
const ProspectingPlanPage = lazy(() => import('@/prospecting/Plans').then(module => ({ default: module.ProspectingPlanPage })));
const ProspectingPlans = lazy(() => import('@/prospecting/Plans').then(module => ({ default: module.ProspectingPlans })));
const Signals = lazy(() => import('@/prospecting/Signals').then(module => ({ default: module.Signals })));
const BuyerPage = lazy(() => import('@/prospecting/Records').then(module => ({ default: module.BuyerPage })));
const IndexEntryPage = lazy(() => import('@/prospecting/Records').then(module => ({ default: module.IndexEntryPage })));
const IntroductionPage = lazy(() => import('@/prospecting/Records').then(module => ({ default: module.IntroductionPage })));
const SignalPage = lazy(() => import('@/prospecting/Records').then(module => ({ default: module.SignalPage })));
const ProspectPage = lazy(() => import('@/prospecting/ProspectPage').then(module => ({ default: module.ProspectPage })));
const SetupProspecting = lazy(() => import('@/prospecting/SetupProspecting').then(module => ({ default: module.SetupProspecting })));
const ShortlistPage = lazy(() => import('@/prospecting/Shortlists').then(module => ({ default: module.ShortlistPage })));
const Shortlists = lazy(() => import('@/prospecting/Shortlists').then(module => ({ default: module.Shortlists })));
const Introductions = lazy(() => import('@/prospecting/FollowThrough').then(module => ({ default: module.Introductions })));
const OurIndex = lazy(() => import('@/prospecting/FollowThrough').then(module => ({ default: module.OurIndex })));
const ProfileChangePage = lazy(() => import('@/prospecting/FollowThrough').then(module => ({ default: module.ProfileChangePage })));
const ProfileChanges = lazy(() => import('@/prospecting/FollowThrough').then(module => ({ default: module.ProfileChanges })));
const Samples = lazy(() => import('@/prospecting/FollowThrough').then(module => ({ default: module.Samples })));
const Buyers = lazy(() => import('@/validation/Validation').then(module => ({ default: module.Buyers })));
const OfferPage = lazy(() => import('@/validation/Validation').then(module => ({ default: module.OfferPage })));
const Offers = lazy(() => import('@/validation/Validation').then(module => ({ default: module.Offers })));
const SupplierPage = lazy(() => import('@/validation/Validation').then(module => ({ default: module.SupplierPage })));
const Suppliers = lazy(() => import('@/validation/Validation').then(module => ({ default: module.Suppliers })));
const Suppression = lazy(() => import('@/outreach/Suppression').then(module => ({ default: module.Suppression })));
const Prospects = lazy(() => import('@/outreach/Prospects').then(module => ({ default: module.Prospects })));
const RefusalPage = lazy(() => import('@/ui/RefusalPage').then(module => ({ default: module.RefusalPage })));
const Refusals = lazy(() => import('@/ui/RefusalPage').then(module => ({ default: module.Refusals })));
const Insight = lazy(() => import('@/insight/Insight').then(module => ({ default: module.Insight })));
const Campaigns = lazy(() => import('@/outreach/Campaigns').then(module => ({ default: module.Campaigns })));
const FirstTouches = lazy(() => import('@/outreach/FirstTouches').then(module => ({ default: module.FirstTouches })));
const Lifecycle = lazy(() => import('@/outreach/Lifecycle').then(module => ({ default: module.Lifecycle })));
const Mailings = lazy(() => import('@/outreach/Mailings').then(module => ({ default: module.Mailings })));
const OutreachReport = lazy(() => import('@/outreach/OutreachReport').then(module => ({ default: module.OutreachReport })));
const TaskRoute = lazy(() => import('@/tasks/TaskPage').then(module => ({ default: module.TaskRoute })));
const Tasks = lazy(() => import('@/tasks/TaskPage').then(module => ({ default: module.Tasks })));

export const router = createBrowserRouter([
  { path: '/signin', element: <SignIn /> },
  {
    path: '/',
    element: <Shell />,
    children: [
      { index: true, element: <Navigate to="/today" replace /> },
      { path: 'today', element: <Today /> },
      // The same mounted chat rail occupies the page here; it is not a second conversation.
      { path: 'chat', element: <div aria-hidden /> },
      { path: 'mail', element: <Threads /> },
      { path: 'mail/shared', element: <Threads view="shared" /> },
      { path: 'mail/mine', element: <Threads view="mine" /> },
      // The department's conversations, asked and answered (the prototype's door).
      { path: 'conversations', element: <Threads title="Conversations" /> },
      { path: 'mail/:threadId', element: <ThreadPage /> },
      { path: 'strangers', element: <Strangers /> },
      { path: 'contacts', element: <Contacts /> },
      { path: 'contacts/:id', element: <ContactPage /> },
      { path: 'companies', element: <Companies /> },
      { path: 'companies/:id', element: <CompanyPage /> },
      // One record per country (ADR-0134): research beside the right to
      // write to someone there. Countries is what Markets used to be called.
      { path: 'markets', element: <Markets /> },
      { path: 'markets/report', element: <ReportDoc /> },
      { path: 'markets/:code', element: <MarketPage /> },
      { path: 'markets/:code/report', element: <ReportDoc /> },
      // Who we look for, where and at what pace (ADR-0134). A prospecting
      // plan is under /prospecting because /plans is the vendors' plans.
      { path: 'profiles', element: <Profiles /> },
      { path: 'profiles/:id', element: <ProfilePage /> },
      { path: 'territories', element: <Territories /> },
      { path: 'territories/:id', element: <TerritoryPage /> },
      { path: 'prospecting/plans', element: <ProspectingPlans /> },
      { path: 'prospecting/plans/:id', element: <ProspectingPlanPage /> },
      { path: 'prospecting/shortlists', element: <Shortlists /> },
      { path: 'prospecting/shortlists/:id', element: <ShortlistPage /> },
      { path: 'signals', element: <Signals /> },
      { path: 'signals/:id', element: <SignalPage /> },
      { path: 'prospecting/setup', element: <SetupProspecting /> },
      // What a plan's finds became (ADR-0136).
      { path: 'profile-changes', element: <ProfileChanges /> },
      { path: 'profile-changes/:id', element: <ProfileChangePage /> },
      { path: 'introductions', element: <Introductions /> },
      { path: 'introductions/:id', element: <IntroductionPage /> },
      { path: 'samples', element: <Samples /> },
      { path: 'our-index', element: <OurIndex /> },
      { path: 'our-index/:id', element: <IndexEntryPage /> },
      // Market validation (ADR-0135): who buys, what the supplier put in
      // writing, and the offer that rests on it.
      { path: 'buyers', element: <Buyers /> },
      { path: 'buyers/:id', element: <BuyerPage /> },
      { path: 'suppliers', element: <Suppliers /> },
      { path: 'suppliers/:id', element: <SupplierPage /> },
      { path: 'offers', element: <Offers /> },
      { path: 'offers/:id', element: <OfferPage /> },
      { path: 'said-stop', element: <Suppression /> },
      { path: 'prospects', element: <Prospects /> },
      { path: 'prospects/:id', element: <ProspectPage /> },
      { path: 'first-touches', element: <FirstTouches /> },
      { path: 'campaigns', element: <Campaigns /> },
      { path: 'lifecycle', element: <Lifecycle /> },
      { path: 'mailings', element: <Mailings /> },
      { path: 'outreach-report', element: <OutreachReport /> },
      { path: 'records/:entity', element: <FlexList /> },
      { path: 'records/:entity/:id', element: <FlexRecord /> },
      { path: 'requests', element: <Requests /> },
      { path: 'requests/:id', element: <RequestPage /> },
      { path: 'activity', element: <Activity /> },
      { path: 'changes', element: <Changes /> },
      { path: 'projects', element: <Projects /> },
      { path: 'projects/:id', element: <ProjectPage /> },
      { path: 'changes/:id', element: <ChangePage /> },
      // The change working in its own sandbox, opened in a new tab (ADR-0150).
      { path: 'changes/:id/preview', element: <ChangePreview /> },
      // A twin's tasks, its person's alone (Amendment 0011, ADR-0139).
      // Tasks (ADR-0149): the board of the person's work, their twin's
      // beside it, and each task's own page.
      { path: 'tasks', element: <Tasks /> },
      { path: 'tasks/:taskId', element: <TaskRoute /> },
      // Oversight (S4, ADR-0073). Each screen shows what the database says
      // the viewer may see; the controls behind the admin door render for
      // a superadmin only, and refuse anybody else at the edge as well.
      { path: 'approvals', element: <Approvals /> },
      { path: 'notifications', element: <Notifications /> },
      { path: 'people', element: <People /> },
      { path: 'people/:id', element: <PersonPage /> },
      { path: 'erasure', element: <Erasures /> },
      { path: 'erasure/:id', element: <ErasurePage /> },
      { path: 'incidents', element: <Incidents /> },
      { path: 'incidents/:id', element: <IncidentPage /> },
      { path: 'agents', element: <Agents /> },
      { path: 'agents/:id', element: <AgentPage /> },
      { path: 'assignments', element: <Assignments /> },
      { path: 'departments', element: <Departments /> },
      { path: 'departments/:name', element: <DepartmentPage /> },
      { path: 'tools', element: <Tools /> },
      { path: 'connections', element: <Connections /> },
      // A superadmin's twin keeping the platform (Amendment 0013, ADR-0172).
      { path: 'operations', element: <Operations /> },
      // What each vendor gives free, and what may be paid (ADR-0095).
      { path: 'plans', element: <Plans /> },
      { path: 'readiness', element: <Readiness /> },
      { path: 'workspace', element: <Workspace /> },
      { path: 'audit', element: <Audit /> },
      { path: 'reports', element: <Reports /> },
      { path: 'safety', element: <Safety /> },
      { path: 'settings', element: <Settings /> },
      { path: 'settings/availability', element: <Availability /> },
      { path: 'settings/mail-rules', element: <MailRules /> },
      { path: 'directions', element: <Directions /> },
      { path: 'registers', element: <Registers /> },
      { path: 'institutions', element: <Institutions /> },
      { path: 'institutions/:id', element: <InstitutionPage /> },
      { path: 'promotions', element: <Promotions /> },
      { path: 'ledger/constitution', element: <Constitution /> },
      { path: 'ledger/decisions', element: <Decisions /> },
      { path: 'ledger/decisions/:number', element: <Decision /> },
      { path: 'ledger/proposals', element: <LedgerProposals /> },
      { path: 'ledger/proposals/:id', element: <LedgerProposalPage /> },
      { path: 'refusals', element: <Refusals /> },
      { path: 'insight/:key', element: <Insight /> },
      {
        path: '*',
        element: <NoSuchPage />,
      },
    ],
  },
]);

/** The address the person is on now, read when the page renders, not when the router was made. */
function NoSuchPage() {
  const { pathname } = useLocation();
  return <RefusalPage failure={{ kind: 'not_found', path: pathname, page: true }} />;
}
